import { createContext, useContext } from 'react';
import { useIsArtifactFocusView } from '@cotera/client/v0/chat/artifact-panel-context';

export type ResourcePageShellLayout = 'panel' | 'flat';

/**
 * The ground a resource page sits on.
 *
 * - `reader` — a listing or a read-only view. The Library's own warm canvas, so
 *   a folder reads the same opened as an artifact as it does from the Library.
 * - `editor` — a resource you can change: a document, an agent, a workflow, a
 *   dataset. White, the way a page you write on is white, wherever it is opened
 *   from — its own page, the split, or the focus view.
 *
 * `reader` is the default because it is the app's ground: a page says so when it
 * is something you edit, not when it is something you read.
 */
export type ResourcePageSurface = 'reader' | 'editor';

/**
 * How a resource page frames itself, given how it was opened.
 *
 * Three surfaces, two answers. Opened from the Library it is a page and floats
 * in a bordered card. Opened as half of a chat's split it is an artifact, and
 * the panel around it is already the card, so it drops its own and renders flat.
 * Opened in the focus view it is an artifact by provenance and a page by
 * presentation: nothing else frames it, so the card comes back.
 *
 * The pages keep `mode: 'artifact'` in that last case rather than flipping to
 * `'page'`, because `mode` also decides whether they own their Share and whether
 * they nest a transient panel, and only the framing changes. See
 * {@link useIsArtifactFocusView}.
 */
export const useResourcePageLayout = (
  mode: 'artifact' | 'page'
): ResourcePageShellLayout => {
  const isFocusView = useIsArtifactFocusView();
  return mode === 'artifact' && !isFocusView ? 'flat' : 'panel';
};

/**
 * The reading measure prose-shaped content sits in.
 *
 * The same cap everywhere the content appears, the focus view included. It
 * briefly dropped there, on the reasoning that an artifact filling the pane
 * would line the types up with the folder table beside it. What that actually
 * produced was a document whose lines ran the whole width of a wide display,
 * which is the thing a measure exists to prevent — a table wants the room, a
 * paragraph does not, and the width they share is not worth prose nobody can
 * follow.
 *
 * Below `md` there is nothing to cap — the viewport is narrower than the cap
 * itself — so the breakpoint costs nothing and says what it means.
 *
 * A constant rather than a hook, so the width has one home without every reader
 * having to be a component to ask for it: the last time this rule was spelled
 * out per page two of them had it wrong.
 */
export const RESOURCE_MEASURE = 'md:max-w-5xl mx-auto';

/**
 * True once an ancestor {@link ResourcePageShell} has drawn the card, so a
 * nested one renders flat instead of framing the same content twice.
 *
 * A resource page can host another whole resource page: a workflow's stack
 * views embed the dataset a trigger watches and the agent behind a node, and
 * they do it by rendering that resource's own page. The inner page cannot know
 * from `mode` alone whether it needs a card — that depends on what is above it,
 * which is what this reports.
 *
 * The same shape as {@link useUnifiedBarRendered}, and for the same reason: the
 * bar is claimed once per screen, and so is the card.
 */
const ResourcePageCardDrawnContext = createContext<boolean>(false);

export const useResourcePageCardDrawn = (): boolean =>
  useContext(ResourcePageCardDrawnContext);

export const ResourcePageCardDrawnProvider =
  ResourcePageCardDrawnContext.Provider;
